import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  ElementRef,
  inject,
  input,
  model,
} from '@angular/core';

/** Figma's `Type` property. */
export type TabsType = 'fill' | 'line' | 'segmented';

/** Figma's `Size` property. */
export type TabsSize = 'sm' | 'md' | 'lg';

/** Figma's `Shape` property, on Fill and Segmented. Line has no box to round. */
export type TabsShape = 'rounded' | 'pill';

/**
 * One tab. A native `<button>`, so Enter, Space and `disabled` are the platform's;
 * the roving tabindex and `aria-selected` come from whether it is the selected one.
 */
@Component({
  selector: 'button[ui-tab]',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './tab.scss',
  host: {
    class: 'ui-tab',
    type: 'button',
    role: 'tab',
    '[class.ui-tab--active]': 'active()',
    '[attr.aria-selected]': 'active()',
    '[attr.tabindex]': 'tabbable() ? 0 : -1',
    '[disabled]': 'disabled() || null',
    '(click)': 'tabs?.select(value())',
  },
  template: '<span class="ui-tab__label"><ng-content /></span>',
})
export class Tab {
  /** Identifies the tab in `selected`. */
  readonly value = input.required<string>();

  readonly disabled = input(false, { transform: booleanAttribute });

  protected readonly tabs = inject(Tabs, { optional: true });

  private readonly element = inject<ElementRef<HTMLButtonElement>>(ElementRef);

  protected readonly active = computed(() => this.tabs?.selected() === this.value());

  /** One tab stop for the whole set: the selected tab, or the first if none is. */
  protected readonly tabbable = computed(
    () =>
      this.active() ||
      (this.tabs?.selected() === undefined && (this.tabs?.isFirstEnabled(this) ?? false)),
  );

  focus(): void {
    this.element.nativeElement.focus();
  }
}

/**
 * Tabs — Figma's `Tab` (node 228:12932): Type(Fill/Line/Segmented) × Size(sm/md/lg),
 * with per-item sets underneath adding Shape(Rounded/Pill) and Style(Primary/
 * Secondary).
 *
 * ```html
 * <div ui-tabs [(selected)]="tab" type="line">
 *   <button ui-tab value="posts">Posts</button>
 *   <button ui-tab value="drafts">Drafts <ui-badge size="sm">02</ui-badge></button>
 * </div>
 *
 * @switch (tab()) { @case ('posts') { … } @case ('drafts') { … } }
 * ```
 *
 * The three types differ only in how the selected tab is drawn — a bordered box, an
 * underline, or a white pill on a grey track — so they are one component with a
 * `type`, not three.
 *
 * **It is a tablist, so the keyboard has to work**: arrows move between tabs, Home
 * and End jump to the ends, and the set is a single tab stop via a roving tabindex.
 * Figma specifies none of that, as usual.
 *
 * Panels stay the app's: `aria-controls` needs an id only the app has, and `@switch`
 * over the selected value is both simpler and lazier than anything this component
 * could offer.
 */
@Component({
  selector: '[ui-tabs]',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './tabs.scss',
  host: {
    '[class]': 'hostClasses()',
    role: 'tablist',
    '(keydown)': 'onKeydown($event)',
  },
  template: '<ng-content />',
})
export class Tabs {
  readonly type = input<TabsType>('fill');
  readonly size = input<TabsSize>('md');
  readonly shape = input<TabsShape>('rounded');

  /** The selected tab's `value`. Two-way. */
  readonly selected = model<string | undefined>(undefined);

  /**
   * Declared after `Tab` in this file for a reason: a signal query's token is read
   * while the class is being defined, and a forward reference would still be in the
   * temporal dead zone.
   */
  private readonly tabs = contentChildren(Tab);

  protected readonly hostClasses = computed(
    () => `ui-tabs ui-tabs--${this.type()} ui-tabs--${this.size()} ui-tabs--${this.shape()}`,
  );

  /** True when nothing is selected yet, so the first tab takes the tab stop. */
  isFirstEnabled(tab: Tab): boolean {
    return this.tabs().find((candidate) => !candidate.disabled()) === tab;
  }

  select(value: string): void {
    this.selected.set(value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const enabled = this.tabs().filter((tab) => !tab.disabled());
    if (!enabled.length) return;

    const current = enabled.findIndex((tab) => tab.value() === this.selected());
    const from = current === -1 ? 0 : current;
    let next: number;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (from - 1 + enabled.length) % enabled.length;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        next = (from + 1) % enabled.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = enabled.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const tab = enabled[next];
    this.select(tab.value());
    // Selection follows focus, which is the pattern for tabs whose panels are cheap
    // — and these are, since the app renders them with @switch.
    tab.focus();
  }
}
